import { countries } from '@frontend/features/common/constants/countries';

import type { RuteroPassengerPayload } from './queries';

/**
 * El país de la agencia, como el código ISO-2 con el que se cargan los campos
 * de país del formulario de reserva.
 *
 * `Agency.country` es un `varchar` en la entidad, pero NO es texto libre: el
 * formulario de agencia lo guarda desde esta misma lista cerrada
 * (`EditAgency/Form.tsx`, `value={country.name}`), que trae `code` y
 * `phoneCode` al lado del `name`. Por eso el mapeo no adivina nada: resuelve
 * contra la lista con la que se guardó.
 *
 * `undefined` cuando no matchea ninguna entrada —filas viejas, o creadas por
 * `local:clients:create` con un nombre a mano—. Ahí no se prellena nada: un
 * país inventado en la nacionalidad de un pasajero viaja al proveedor.
 */
export function agencyCountryCode(
  country: string | null | undefined,
): string | undefined {
  const normalized = normalize(country);
  if (!normalized) return undefined;
  return countries.find((entry) => normalize(entry.name) === normalized)?.code;
}

/** Sin acentos, sin mayúsculas y sin espacios de borde: `" Perú "` es `PE`. */
function normalize(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Siembra el país de la agencia en las nacionalidades que siguen VACÍAS.
 *
 * Punto único de los DOS caminos por los que entra, que es lo que evita que
 * uno lo aplique y el otro no: al armar las filas (cuando el país ya se
 * conoce) y cuando la query lo resuelve DESPUÉS de armarlas, que es lo normal
 * en una carga fría. Sin el segundo, el modal se abría con la nacionalidad
 * vacía y ese dato no viajaba en el hold salvo que alguien lo tipeara.
 *
 * Solo siembra donde la oferta DECLARA el campo, es decir donde el formulario
 * lo dibuja: un valor que nadie ve no se puede corregir, y viajaría al
 * proveedor sin que exista el control para desmentirlo. Quien decide si se
 * dibuja es el llamador, pasando el país solo en ese caso.
 *
 * Lo que ya tiene valor no se toca, venga de donde venga: la nacionalidad que
 * comiteó la búsqueda (pases) y la que el asesor haya elegido o borrado a
 * propósito son decisiones, no huecos.
 *
 * Devuelve el MISMO array cuando no hay nada que sembrar: es estado de React y
 * un array nuevo por cada resolución de la query sería un re-render al pedo.
 */
export function withAgencyNationality(
  passengers: RuteroPassengerPayload[],
  country: string | undefined,
): RuteroPassengerPayload[] {
  if (!country) return passengers;
  if (passengers.every((pax) => pax.nationality?.trim())) return passengers;
  return passengers.map((pax) =>
    pax.nationality?.trim() ? pax : { ...pax, nationality: country },);
}
